import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BaseService } from '../shared/services/base.service';
import { BatchDetailPage, BatchFile, BatchPage, BatchStatusDetail, BatchTransaction, FileProcess, FileProcessPage, TransactionPage, TransactionResult, ValidationResult, ValidationRow } from './batch-processing.models';
import { BatchProcessingFilter } from './batch-processing.filter';

// Exact translation of the backend Java regex: "^[\\w\\-.\\\\]+@[\\w\\-.]+\\.[a-zA-Z]{2,}$"
const EMAIL_REGEX = /^[\w\-.\\]+@[\w\-.]+\.[a-zA-Z]{2,}$/;

@Injectable({ providedIn: 'root' })
export class BatchProcessingService {

  // TODO: replace with real endpoint once backend is ready
  private readonly BASE_URL = this.base.HOST + '/portal/batch';
  private readonly UPLOAD_URL = this.base.HOST_UPLOADER + '/portal/upload';

  /** Tracks how many times getBatchStatus has been called — used for mock progress simulation. */
  private statusCallCount = 0;

  constructor(
    private http: HttpClient,
    private base: BaseService,
  ) {}

  /**
   * Returns a paginated list of batch files from the real file-process API.
   */
  getAll(params: HttpParams): Observable<FileProcessPage> {
    const url = `${this.base.HOST_BATCH}/file-process/search`;
    console.log(`[BatchList] GET ${url}?${params.toString()}`);
    return this.http.get<FileProcessPage>(url, { params }).pipe(
      catchError(this.base.handleError)
    );
  }

  /**
   * Uploads a batch file to the encrypt-and-upload endpoint.
   * POST /api/v2/partner/:partnerId/service/:serviceId/plan/:planId/files/encrypt-and-upload
   */
  upload(payload: FormData): Observable<{ code: number; message: string; success: boolean; date: string; result: any; pager: any }> {
    const partnerId = payload.get('partner_id');
    const serviceId = payload.get('service_id');
    const planId    = payload.get('plan_id');
    const url = `${this.base.HOST_BATCH}/partner/${partnerId}/service/${serviceId}/plan/${planId}/files/encrypt-and-upload`;

    return this.http.post<{ code: number; message: string; success: boolean; date: string; result: any; pager: any }>(url, payload).pipe(
      catchError(this.base.handleError)
    );
  }

  /**
   * Returns paginated transaction rows for a given file process and control file.
   * GET /api/v2/transactions/process-file/:fileProcessId/control-file/:controlFileId
   */
  getTransactions(fileProcessId: number, controlFileId: number, params: HttpParams): Observable<TransactionPage> {
    const url = `${this.base.HOST_BATCH}/transactions/process-file/${fileProcessId}/control-file/${controlFileId}`;
    return this.http.get<TransactionPage>(url, { params }).pipe(
      catchError(this.base.handleError)
    );
  }

  /**
   * Returns a presigned download URL for the response CSV.
   * TODO: implement when backend exposes the download endpoint.
   */
  getDownloadUrl(batchId: string): Observable<{ url: string }> {
    // return this.http.get<{ url: string }>(`${this.BASE_URL}/${batchId}/download`).pipe(
    //   catchError(this.base.handleError)
    // );
    return of({ url: '#' });
  }

  /**
   * Returns the processing status of a batch.
   * TODO: replace with real GET /portal/batch/:id when backend is ready.
   * Mock simulates progress advancing on each call.
   */
  getBatchStatus(batchId: string): Observable<BatchStatusDetail> {
    // return this.http.get<BatchStatusDetail>(`${this.BASE_URL}/${batchId}`).pipe(
    //   catchError(this.base.handleError)
    // );

    this.statusCallCount++;
    const total     = 1245;
    const step      = 180;
    const processed = Math.min(this.statusCallCount * step, total);
    const pending   = total - processed;
    const failed    = Math.floor(processed * 0.06);
    const ok        = processed - failed;
    const isDone    = processed >= total;

    const resultCycle: TransactionResult[] = ['OK', 'OK', 'OK', 'ERROR', 'OK', 'REINTENTO', 'OK'];
    const recentRows = Array.from({ length: Math.min(processed, 5) }, (_, i) => {
      const row = Math.max(processed - 4 + i, 1);
      const res: TransactionResult = resultCycle[(row + i) % resultCycle.length];
      const now  = new Date();
      const pad  = (n: number, len = 2) => ('000' + n).slice(-len);
      const time = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds() + i)}`;
      return {
        row,
        cliente_id: ('0000' + (1000 + row)).slice(-4),
        factura:    `26091040${('000' + row).slice(-3)}`,
        monto:      `$${(50 + (row % 400)).toFixed(2)}`,
        resultado:  res,
        recibo_error: res === 'OK'
          ? `RCPT-${660000 + row}`
          : res === 'REINTENTO'
          ? 'Tiempo de espera agotado del gateway'
          : 'Fondos insuficientes',
        processed_at: time,
      };
    });

    const mock: BatchStatusDetail = {
      batch_id:            batchId,
      file_name:           'cobros_20260401.txt',
      merchant_name:       'Banco General',
      status:              isDone
        ? (failed > 0 ? 'COMPLETED_WITH_ERRORS' : 'COMPLETED')
        : (this.statusCallCount === 1 ? 'PENDING' : 'PROCESSING'),
      started_at:          new Date().toISOString().replace('T', ' ').slice(0, 19),
      total,
      processed,
      pending,
      ok,
      failed,
      recent_transactions: recentRows,
    };

    return of(mock);
  }

  resetStatusMock(): void {
    this.statusCallCount = 0;
  }

  getBatchDetail(batchId: string, pageIndex: number, pageSize: number, resultado = ''): Observable<BatchDetailPage> {
    const results: TransactionResult[] = ['OK', 'OK', 'OK', 'ERROR', 'REINTENTO'];
    const receipts = ['RCPT-661000', 'RCPT-661001', 'RCPT-661002', 'Fondos insuficientes', 'Tiempo agotado'];
    const montos   = ['$52.10', '$145.00', '$88.34', '$390.00', '$78.99', '$200.00', '$34.50', '$99.00'];
    const pad = (n: number, len = 2) => ('0000' + n).slice(-len);

    const total = 1245;
    const allRows: BatchTransaction[] = [];
    for (let i = 0; i < total; i++) {
      const idx = i % results.length;
      allRows.push({
        row:          i + 1,
        cliente_id:   ('00000' + (1000 + i)).slice(-5),
        factura:      `26091040${pad(i, 3)}`,
        monto:        montos[i % montos.length],
        resultado:    results[idx],
        recibo_error: receipts[idx],
        processed_at: `14:${pad(30 + Math.floor(i / 60))}:${pad(i % 60)}`,
      });
    }

    const filtered = resultado ? allRows.filter(r => r.resultado === resultado) : allRows;
    const start    = pageIndex * pageSize;
    const sliced   = filtered.slice(start, start + pageSize);

    return of({
      batch_id:   batchId,
      file_name:  'cobros_20260401.txt',
      bank_name:  'Banco General',
      status:     'COMPLETED_WITH_ERRORS',
      started_at: '2026-04-01 14:31:02',
      total,
      ok:      1101,
      failed:   109,
      retried:   35,
      result:   sliced,
      pager:    { totalEntities: filtered.length, pageIndex, pageSize },
    });
  }

  /** Client-side pre-validation of a plain-text or CSV file. */
  validateFile(file: File): Observable<ValidationResult> {
    return new Observable(observer => {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const text   = (e.target?.result as string) ?? '';
        const lines  = text.split('\n').filter(l => l.trim().length > 0);
        const ext    = file.name.split('.').pop()?.toLowerCase();
        const format = ext === 'csv' ? 'CSV delimitado por |' : 'TXT delimitado por |';

        const rows = lines.map((raw, i) => this.validateRow(raw, i + 1));
        const firstCols = lines.length > 0 ? lines[0].split('|').length : 0;

        observer.next({
          total:            rows.length,
          valid:            rows.filter(r => r.valid).length,
          invalid:          rows.filter(r => !r.valid).length,
          rows,
          fileFormat:       format,
          columnsDetected:  firstCols,
        });
        observer.complete();
      };
      reader.onerror = () => observer.error('Error al leer el archivo');
      reader.readAsText(file);
    });
  }

  private validateRow(raw: string, rowNumber: number): ValidationRow {
    const cols   = raw.trim().split('|');
    const errors: string[] = [];

    if (cols.length < 9 || cols.length > 11) {
      errors.push(`Se esperan 9, 10 u 11 columnas, se encontraron ${cols.length}`);
      return { row: rowNumber, raw, valid: false, errors };
    }

    const [c0, c1, c2, c3, c4, c5, c6, c7, c8, c9 = '', c10 = ''] = cols.map(c => c.trim());

    // Col 1 — Correlativo: no vacío, numérico
    if (!c0) errors.push('Correlativo vacío');
    else if (!/^\d+$/.test(c0)) errors.push('Correlativo debe ser numérico');

    // Col 2 — Identificador interno: requerido; letras/números/guion medio; sin espacios
    const cedula = c1;
    if (!cedula) errors.push('Identificador vacío');
    else if (!/^[A-Za-z0-9-]+$/.test(cedula)) errors.push('Identificador inválido');

    // Col 3 — Fecha de cobro: YYYYMMDD válida
    let fechaCobro = c2;
    if (!/^\d{8}$/.test(c2)) {
      errors.push('Formato de fecha de cobro inválido (esperado YYYYMMDD)');
    } else {
      const d = new Date(`${c2.slice(0,4)}-${c2.slice(4,6)}-${c2.slice(6,8)}`);
      if (isNaN(d.getTime())) errors.push('Fecha de cobro inválida');
      else if (!this.isValidCalendarDate(c2)) errors.push('Fecha de cobro inválida');
      else fechaCobro = `${c2.slice(0,4)}-${c2.slice(4,6)}-${c2.slice(6,8)}`;
    }

    // Col 4 — Tarjeta: 13–19 dígitos; siempre debe pasar Luhn y no ser un placeholder.
    if (!/^\d{13,19}$/.test(c3)) {
      errors.push('Longitud del número de tarjeta inválida');
    } else if (this.hasRepeatedDigitsOnly(c3)) {
      errors.push('Número de tarjeta inválido');
    } else if (!this.luhn(c3)) {
      errors.push('Número de tarjeta no pasa el algoritmo de Luhn');
    }

    // Col 5 — Expiración: YYYYMM, mes 01–12; debe ser mayor al mes actual
    let expiracion = c4;
    if (!/^\d{6}$/.test(c4)) {
      errors.push('Formato de expiración inválido (esperado YYYYMM)');
    } else {
      const year = parseInt(c4.slice(0, 4), 10);
      const month = parseInt(c4.slice(4, 6), 10);

      if (month < 1 || month > 12) {
        errors.push('Mes de expiración inválido');
      } else {
        expiracion = `${c4.slice(0,4)}-${c4.slice(4,6)}`;

        const expYm = year * 100 + month;
        const now = new Date();
        const nowYm = now.getFullYear() * 100 + (now.getMonth() + 1);
        if (expYm <= nowYm) {
          errors.push('La expiración debe ser mayor al mes actual');
        }
      }
    }

    // Col 6 — Referencia: no vacía (puede contener letras)
    if (!c5) errors.push('Referencia vacía');

    // Col 7 — Tarjetaholder: no vacío + normalización (tildes/ñ) + reglas de nombre
    if (!c6) {
      errors.push('Falta el nombre del tarjetahabiente');
    } else {
      const normalizedName = this.normalizeCardholderName(c6);
      errors.push(...this.validateCardholderName(normalizedName));
    }

    // Col 8 — Monto: número > 0
    const monto = this.parseStrictNumber(c7);
    if (monto === null)  errors.push('El monto debe ser numérico');
    else if (monto <= 0) errors.push('El monto debe ser mayor a 0');

    // Col 9 — Impuestos: número >= 0
    const impuestos = this.parseStrictNumber(c8);
    if (impuestos === null) errors.push('Los impuestos deben ser numéricos');
    else if (impuestos < 0) errors.push('Los impuestos no pueden ser negativos');

    // Col 10 — DNI opcional: si viene informado, solo permite letras, números y guiones.
    if (c9 && !/^[A-Za-z0-9-]+$/.test(c9)) {
      errors.push('DNI inválido en columna 9');
    }

    // Col 11 — Email opcional: si viene informado, debe cumplir el formato de correo.
    const email = c10;
    if (email && !EMAIL_REGEX.test(email)) {
      errors.push('Email inválido en columna 10');
    }

    return {
      row:          rowNumber,
      raw,
      valid:        errors.length === 0,
      errors,
      cedula,
      // En este formato, el "Cliente ID" corresponde a la referencia (col 6).
      // La col 1 es solo correlativo.
      clienteId:    c5,
      fechaCobro,
      tarjeta:      c3,
      expiracion,
      factura:      c5,
      tarjetaHolder: c6 ? this.normalizeCardholderName(c6) : c6,
      monto:        monto === null ? c7 : `$${monto.toFixed(2)}`,
      impuestos:    impuestos === null ? c8 : `$${impuestos.toFixed(2)}`,
      dni:           c9,
      email,
    };
  }

  private isValidCalendarDate(value: string): boolean {
    const year = parseInt(value.slice(0, 4), 10);
    const month = parseInt(value.slice(4, 6), 10);
    const day = parseInt(value.slice(6, 8), 10);

    if (month < 1 || month > 12 || day < 1) { return false; }

    const isLeapYear = year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0);
    const daysInMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return day <= daysInMonth[month - 1];
  }

  private parseStrictNumber(value: string): number | null {
    const input = (value ?? '').toString().trim();
    if (!input) { return null; }

    // Reject alphabetic characters so "362.a" / "100USD" no pasen.
    if (/[A-Za-z]/.test(input)) { return null; }

    // Only allow digits, separators, sign, currency, whitespace.
    if (/[^0-9\s.,\-$]/.test(input)) { return null; }

    let s = input.replace(/\s/g, '');
    s = s.replace(/\$/g, '');

    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');

    if (lastComma !== -1 && lastDot !== -1) {
      // Decide decimal separator by the last occurrence.
      if (lastComma > lastDot) {
        // 1.234,56 -> 1234.56
        s = s.replace(/\./g, '').replace(/,/g, '.');
      } else {
        // 1,234.56 -> 1234.56
        s = s.replace(/,/g, '');
      }
    } else if (lastComma !== -1) {
      // 123,45 -> 123.45
      s = s.replace(/,/g, '.');
    }

    if (!/^-?\d+(?:\.\d+)?$/.test(s)) { return null; }

    const num = Number(s);
    if (!isFinite(num)) { return null; }
    return num;
  }

  private validateCardholderName(name: string): string[] {
    const errors: string[] = [];
    const trimmed = (name ?? '').toString().trim();

    if (trimmed.length > 50) {
      errors.push('Máximo 50 caracteres');
    }

    // Solo letras A-Z y espacios (sin tildes ni ñ)
    if (!/^[A-Za-z ]+$/.test(trimmed)) {
      errors.push('Solo letras y espacios permitidos (sin tildes ni ñ)');
    }

    const words = trimmed.split(/\s+/).filter(Boolean);
    if (words.length < 2) {
      errors.push('Debe incluir nombre y apellido');
      return errors;
    }

    if ((words[0]?.length ?? 0) < 3) {
      errors.push('El nombre debe tener al menos 3 caracteres');
    }

    const hasMainLastName = words.slice(1).some(word => word.length >= 3);
    if (!hasMainLastName) {
      errors.push('El apellido debe tener al menos 3 caracteres');
    }

    return errors;
  }

  private normalizeCardholderName(name: string): string {
    const raw = (name ?? '').toString().trim();
    if (!raw) { return raw; }

    // Primero convertimos ñ/Ñ explícitamente a n/N.
    const withoutEnye = raw.replace(/ñ/g, 'n').replace(/Ñ/g, 'N');

    // Luego removemos diacríticos: áéíóúü -> aeiouu (manteniendo la letra base)
    // NFD separa letra + marca; luego eliminamos las marcas.
    return withoutEnye.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  /** Luhn algorithm — returns true if the card number is valid */
  private luhn(cardNumber: string): boolean {
    let sum = 0;
    let alternate = false;
    for (let i = cardNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cardNumber[i], 10);
      if (alternate) {
        digit *= 2;
        if (digit > 9) { digit -= 9; }
      }
      sum += digit;
      alternate = !alternate;
    }
    return sum % 10 === 0;
  }

  private hasRepeatedDigitsOnly(cardNumber: string): boolean {
    return /^(\d)\1+$/.test(cardNumber);
  }
}
